import {
  EmailEngineEmailType,
  ExtendedEmailEngineType,
} from '@/types/emailengine';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import ReplyActionBar from './ReplyActionBar';

function ViewConversationThread({
  account,
  threadId,
  emails,
  receiverInbox,
  handleCloseConversation,
}: {
  account: string;
  threadId: string;
  emails: EmailEngineEmailType[];
  receiverInbox: string;
  handleCloseConversation: () => void;
}): React.ReactNode {
  console.log('ViewConversationThread received:', { 
    account, 
    threadId, 
    emails, 
    emailsCount: emails.length,
    receiverInbox 
  });
  
  const [emailBodies, setEmailBodies] = useState({});
  const [receiverEmail, setReceiverEmail] = useState('');
  const [populatedEmails, setPopulatedEmails] =
    useState<ExtendedEmailEngineType[]>(emails);

  useEffect(() => {
    console.log('Setting populatedEmails from emails prop:', emails);
    setPopulatedEmails(emails);
  }, [emails]);

  // useEffect(() => {
  //   console.log('THE POPULATED EMAILS', populatedEmails);
  // }, [populatedEmails]);

  // useEffect(() => {
  //   console.log('THE EMAILS BODIES FETCHED', emailBodies);
  // }, [emailBodies]);

  useEffect(() => {
    const fetchEmailBodies = async () => {
      console.log('Starting to fetch email bodies for emails:', emails);
      const updatedBodies: Record<string, Record<string, string>> = {};

      if (emails.length === 0) {
        console.log('No emails to fetch bodies for');
        return {};
      }

      await Promise.all(
        emails.map(
          async (
            email
          ): Promise<{
            success: boolean;
            data: { html: string; plain: string; hasMore: boolean };
            textId: string;
          } | null> => {
            try {
              console.log('Fetching body for email:', email.id, 'textId:', email.text.id);
              const queryParams = new URLSearchParams({
                textId: email.text.id ?? '',
                emailId: account ?? '',
              });
              const res = await fetchWithRewrites(
                `/api/v1/emailengine/text?${queryParams.toString()}`
              );
              const data = await res.json();
              console.log('Fetched email body data for email:', email.id, data);

              updatedBodies[email.id] = {
                html: data.html ?? '',
                text: data.plain ?? '',
              };
              return { success: true, data: data, textId: email.text.id };
            } catch (error) {
              console.error('Error fetching email body for email:', email.id, error);
              return {
                success: false,
                data: { html: '', plain: '', hasMore: false },
                textId: '',
              };
            }
          }
        )
      );

      console.log('All email bodies fetched, updatedBodies:', updatedBodies);
      setEmailBodies(updatedBodies);
      return updatedBodies;
    };

    fetchEmailBodies().then((data) => {
      console.log('fetchEmailBodies completed, updating populatedEmails');
      const updatedBodies = data;
      setPopulatedEmails((prev) =>
        prev.map((email) => {
          const matchingTextData = updatedBodies[email.id];
          console.log('Updating email:', email.id, 'with text data:', matchingTextData);

          if (!matchingTextData) {
            console.log('No matching text data for email:', email.id);
            return email;
          }

          return {
            ...email,
            html: matchingTextData.html || email.html,
            textPlain: matchingTextData.text || '',
          };
        })
      );
    });
  }, [emails, account]);

  return (
    <Stack maxHeight="80vh" overflow="auto">
      {/* Header with close button */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6">Conversation Thread</Typography>
        <IconButton 
          onClick={handleCloseConversation}
          size="small"
          sx={{ 
            '&:hover': { 
              backgroundColor: 'action.hover' 
            } 
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Stack
        height="65vh"
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {emails.length === 0 ? (
          <Typography>No emails found for this thread</Typography>
        ) : (
          emails.map((email, idx) => {
            console.log('Rendering email:', email.id, 'subject:', email.subject);
            return (
              <Box key={`email-${email.id}-${idx}`} my={2} mt={6}>
                <Divider />
                <Card
                  sx={{
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography variant="h4">{email.subject}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    From: {email.from?.name || email.from?.address} | 
                    Date: {new Date(email.date).toLocaleString()}
                  </Typography>
                  <CardContent>
                    <div
                      dangerouslySetInnerHTML={{
                        // @ts-ignore
                        __html: emailBodies[email.id]?.html
                          ? // @ts-ignore
                            emailBodies[email.id].html
                          : '<p>Loading...</p>',
                      }}
                    />
                  </CardContent>
                </Card>
              </Box>
            );
          })
        )}
      </Stack>
      <Box bottom={0} right={0} width="100%">
        <ReplyActionBar
          sender={account}
          receiverInbox={receiverInbox}
          emailsInThread={populatedEmails}
          threadId={threadId}
        />
      </Box>
    </Stack>
  );
}

export default ViewConversationThread;
